import { mfaVerifyRequestSchema } from '@an-crm/shared';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Smartphone } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { OtpInput } from '@/components/ui/otp-input';
import { friendlyMessage } from '@/lib/api/api-error';
import { authApi, type TokenResponse } from './api';
import { FormAlert } from './form-alert';

export interface MfaCodeFormProps {
  challengeToken: string;
  onVerified: (token: TokenResponse) => void;
  submitLabel?: string;
}

/** 6-digit code entry that auto-submits once complete. Shared by MFA verify and MFA setup. */
export function MfaCodeForm({ challengeToken, onVerified, submitLabel = 'Verify' }: MfaCodeFormProps) {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const verify = useMutation({
    mutationFn: authApi.mfaVerify,
    onSuccess: onVerified,
    onError: () => setCode(''),
  });

  const submit = (value: string) => {
    const parsed = mfaVerifyRequestSchema.safeParse({ challengeToken, code: value });
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message ?? 'Enter the 6-digit code');
      return;
    }
    setLocalError(null);
    verify.mutate(parsed.data);
  };

  const message = localError ?? (verify.error ? friendlyMessage(verify.error) : null);

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit(code);
      }}
      className="flex flex-col gap-5"
    >
      <FormAlert message={message} />
      <OtpInput
        value={code}
        onChange={(v) => {
          setCode(v);
          if (localError) setLocalError(null);
        }}
        onComplete={(v) => {
          if (!verify.isPending) submit(v);
        }}
        invalid={Boolean(message)}
        disabled={verify.isPending}
        autoFocus
      />
      <Button
        type="submit"
        size="lg"
        className="w-full"
        loading={verify.isPending}
        disabled={code.length < 6}
      >
        {submitLabel}
      </Button>
    </form>
  );
}

export function MfaVerifyStep({
  challengeToken,
  onVerified,
  onBack,
}: {
  challengeToken: string;
  onVerified: (token: TokenResponse) => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <StepHeading
        icon={<Smartphone className="size-5" aria-hidden />}
        title="Two-step verification"
        description="Enter the 6-digit code from your authenticator app."
      />
      <MfaCodeForm challengeToken={challengeToken} onVerified={onVerified} />
      <BackButton onClick={onBack} />
    </div>
  );
}

export function StepHeading({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className="flex size-11 items-center justify-center rounded-xl bg-primary-soft text-primary-text ring-1 ring-brand/15">
        {icon}
      </span>
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[26px] leading-8 font-semibold text-fg">{title}</h1>
        <p className="text-sm text-fg-muted">{description}</p>
      </div>
    </div>
  );
}

export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick} className="self-center">
      <ArrowLeft aria-hidden /> Use a different account
    </Button>
  );
}
